import { OpeningDetailResponse } from 'api-clients/monolith/models/OpeningDetailResponse';
import type { PostingRequirementsDef } from './posting-requirements/requirements-key';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';
import { ChevronRightIcon } from 'lucide-react';

import CreditCardIcon from '@/assets/icons/credit-card.svg';
import LocationMarkerIcon from '@/assets/icons/location-marker.svg';
import { ClockIcon } from '@/components/icons/clock';
import { CircularProgress } from '@/components/ui/circular-progress';
import { Skeleton } from '@/components/ui/skeleton';
import { TruncatedText } from '@/components/ui/truncated-text';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import CheckCircleIcon from '@/assets/icons/check-circle-solid.svg';

import { useJobConstants } from './hooks/use-job-constants';
import { useOpeningProgress } from './hooks/use-opening-progress';

interface SummaryViewProps {
  opening: OpeningDetailResponse;
  payDisplay: string | null;
  contractRequirements: PostingRequirementsDef[];
  toggleExpanded: () => void;
}

export function SummaryView({
  opening,
  payDisplay,
  contractRequirements,
  toggleExpanded,
}: SummaryViewProps) {
  const { getJobHours } = useJobConstants();
  const { hookForm } = useJobPostingContext();
  const { getValues } = hookForm;

  const postingTitle = getValues(`jobDetails.${opening.external_id}.title`);

  const { fieldsLeft, isComplete, progress } = useOpeningProgress({
    opening,
    contractRequirements,
  });

  return (
    <button
      type="button"
      onClick={toggleExpanded}
      className="border-fx-neutral-200 hover:bg-fx-neutral-50 flex items-center justify-between gap-4 rounded-lg border bg-white p-4 text-left shadow-sm"
    >
      {/* Progress ring — left of the opening details */}
      {isComplete ? (
        <img
          src={CheckCircleIcon}
          alt=""
          className="text-fx-green h-5 w-5 shrink-0"
        />
      ) : (
        <CircularProgress value={progress} />
      )}

      <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
        <Typography variant="body2">{postingTitle}</Typography>

        <div className="flex w-full items-center gap-4">
          {(opening.location_address || opening.remote) && (
            <div className="flex min-w-16 shrink items-center gap-1 overflow-hidden">
              <img
                src={LocationMarkerIcon}
                alt="Location Marker"
                className="shrink-0"
              />
              <TruncatedText variant="subtitle1" as="span">
                {opening.location_address || (opening.remote && 'Remote')}
              </TruncatedText>
            </div>
          )}

          {payDisplay && (
            <div className="flex shrink-0 items-center gap-1">
              <img src={CreditCardIcon} alt="Credit Card" />
              <Typography variant="subtitle1">{payDisplay}</Typography>
            </div>
          )}

          {opening.job_hours && (
            <div className="flex shrink-0 items-center gap-1">
              <ClockIcon />
              <Typography variant="subtitle1">
                {getJobHours(opening.job_hours)}
              </Typography>
            </div>
          )}
        </div>
      </div>

      {/* Status text + chevron — right side */}
      <div className="flex shrink-0 items-center gap-2">
        <Typography
          variant="subtitle2"
          className={clsx(isComplete ? 'text-fx-green' : 'text-fx-amber')}
        >
          {isComplete ? (
            <FormattedMessage
              defaultMessage="Complete"
              description="Status label shown on a summary card when all required fields are filled"
            />
          ) : (
            <FormattedMessage
              defaultMessage="{count} {count, plural, one {field} other {fields}} left"
              description="Status label showing how many required fields remain on a summary card"
              values={{ count: fieldsLeft }}
            />
          )}
        </Typography>

        <ChevronRightIcon className="text-fx-neutral-400" />
      </div>
    </button>
  );
}

SummaryView.Skeleton = function SummaryViewSkeleton() {
  return (
    <div className="border-fx-neutral-200 flex items-center justify-between gap-2 rounded-lg border bg-white p-4 shadow-sm">
      <Skeleton className="size-5 shrink-0 rounded-full" />
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
        <Skeleton className="h-5 w-[240px]" />

        <div className="mt-1 flex w-full items-center gap-4">
          <Skeleton className="h-4 w-[320px]" />
          <Skeleton className="h-4 w-[80px]" />
          <Skeleton className="h-4 w-[80px]" />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-[80px]" />
        <ChevronRightIcon className="text-fx-neutral-400" />
      </div>
    </div>
  );
};
